import type { ComponentType, ReactNode } from 'react';
import type { ModuleSummary } from '@ddone/shared-types';

export interface NavigationContribution {
  id: string;
  label: string;
  href: string;
  icon?: ComponentType<{ size?: number; 'aria-hidden'?: boolean | 'true' }>;
  order?: number;
  permission?: string;
}

export interface RouteContribution {
  id: string;
  path: string;
  component: ComponentType;
  permission?: string;
}

export interface DashboardWidgetContribution {
  id: string;
  title: string;
  component: ComponentType;
  permission?: string;
  defaultSize?: 'small' | 'medium' | 'large';
}

export interface CommandContribution {
  id: string;
  label: string;
  keywords?: string[];
  permission?: string;
  run(): void | Promise<void>;
}

export interface SettingsContribution {
  id: string;
  label: string;
  component: ComponentType;
  permission?: string;
}

export interface RegisteredModule {
  id: string;
  navigation?: NavigationContribution[];
  routes?: RouteContribution[];
  dashboardWidgets?: DashboardWidgetContribution[];
  commands?: CommandContribution[];
  settings?: SettingsContribution[];
  badges?: Record<string, ReactNode>;
  dispose?(): void | Promise<void>;
}

export interface ModuleFrontendContext {
  apiBaseUrl: string;
  workspaceId: string;
  locale: string;
  hasPermission(permission: string): boolean;
  navigate(href: string): Promise<void>;
  invalidate(keys?: readonly unknown[]): Promise<void>;
  notify(message: string, variant?: 'info' | 'success' | 'error'): void;
}

export type ModuleRegistration = (context: ModuleFrontendContext) => RegisteredModule;

export class FrontendModuleRegistry {
  #modules = new Map<string, RegisteredModule>();
  #listeners = new Set<() => void>();
  #snapshot: readonly RegisteredModule[] = [];

  register(module: RegisteredModule): void {
    if (!module.id || this.#modules.has(module.id)) {
      throw new Error(`Modulo frontend non valido o già registrato: ${module.id}`);
    }
    this.#modules.set(module.id, Object.freeze(module));
    this.#emit();
  }

  get modules(): readonly RegisteredModule[] {
    return this.#snapshot;
  }

  async load(manifest: ModuleSummary, context: ModuleFrontendContext): Promise<void> {
    if (!manifest.frontendEntry) return;
    const entryUrl = new URL(manifest.frontendEntry, window.location.origin);
    if (entryUrl.origin !== window.location.origin) {
      throw new Error(`Bundle cross-origin rifiutato per ${manifest.id}`);
    }
    const loaded: unknown = await import(/* @vite-ignore */ entryUrl.href);
    if (!isModuleBundle(loaded)) throw new Error(`Bundle frontend invalido: ${manifest.id}`);
    const registration = loaded.registerModule(context);
    if (registration.id !== manifest.id) throw new Error(`ID modulo incoerente: ${manifest.id}`);
    this.register(registration);
  }

  async purge(): Promise<void> {
    const disposals = this.modules.map(async (module) => module.dispose?.());
    await Promise.allSettled(disposals);
    this.#modules.clear();
    this.#emit();
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  snapshot = (): readonly RegisteredModule[] => this.modules;

  #emit(): void {
    this.#snapshot = [...this.#modules.values()];
    this.#listeners.forEach((listener) => listener());
  }
}

function isModuleBundle(value: unknown): value is { registerModule: ModuleRegistration } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'registerModule' in value &&
    typeof value.registerModule === 'function'
  );
}

export function canAccess(
  permission: string | undefined,
  permissions: ReadonlySet<string>
): boolean {
  return !permission || permissions.has('*') || permissions.has(permission);
}
